import React from 'react';
import { motion } from 'framer-motion';
import { type Question } from '../../content';
import { Button } from '../../components/ui/Button';
import { AnswerComparison } from './feedback/AnswerComparison';
import { formatCorrectAnswer, formatUserAnswer } from './feedback/feedbackAnswerFormatters';
import { DjayIdentity } from '../../mascot';
import { getQuestionTypeConfig } from '../../content';
import { ArrowRight, Star } from 'lucide-react';
function isInteractiveQuestion(question?: Question): boolean {
  if (!question) return false;
  return getQuestionTypeConfig(question.questionType || 'multiple_choice').isInteractive;
}
interface FluencyCorrectProps {
  currentQuestionIndex: number;
  speedStreak: number;
  xpGained: number;
}
export function FluencyCorrectState({
  currentQuestionIndex,
  speedStreak,
  xpGained
}: FluencyCorrectProps) {
  return <motion.div key={`fluency-correct-${currentQuestionIndex}`} initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.9
  }} className="flex-1 flex flex-col items-center justify-center py-12" data-id="element-10190">
      <div className="bg-amber-50 border-2 border-amber-200 rounded-3xl p-8 md:p-12 text-center shadow-xl shadow-amber-100/50 max-w-md w-full relative" data-id="element-10191">
        {speedStreak >= 3 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-black uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg" data-id="element-10192">
            {speedStreak}x Snelheidsreeks! ⚡
          </motion.div>}
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        bounce: 0.5,
        delay: 0.1
      }} className="mx-auto mb-6 flex justify-center" data-id="element-10193">
          <DjayIdentity size="lg" emotion="celebrating" data-id="element-10194" />
        </motion.div>
        <h2 className="text-2xl font-black text-amber-800 mb-2" data-id="element-10195">
          {speedStreak >= 3 ? 'Razendsnel! 🔥' : speedStreak > 0 ? 'Lekker bezig! ⚡' : 'Goed gedaan!'}
        </h2>
        {xpGained > 0 && <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-amber-100 shadow-sm mt-4" data-id="element-10196">
            <Star className="w-5 h-5 fill-amber-400 text-amber-400" data-id="element-10197" />
            <span className="font-bold text-amber-700" data-id="element-10198">+{xpGained} XP</span>
          </div>}
      </div>
    </motion.div>;
}
interface FluencyIncorrectProps {
  currentQuestionIndex: number;
  currentQuestion: Question;
  userTypedAnswer: string;
  userAnswerIndex: number | null;
  onShowFullFeedback: () => void;
  onNext: () => void;
}
export function FluencyIncorrectState({
  currentQuestionIndex,
  currentQuestion,
  userTypedAnswer,
  userAnswerIndex,
  onShowFullFeedback,
  onNext
}: FluencyIncorrectProps) {
  const interactive = isInteractiveQuestion(currentQuestion);
  const userAnswer = formatUserAnswer(currentQuestion, interactive ? userTypedAnswer : currentQuestion.options?.[userAnswerIndex ?? 0] || '');
  return <motion.div key={`fluency-incorrect-${currentQuestionIndex}`} initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} className="flex-1 flex flex-col items-center justify-center py-8 px-4" data-id="element-10199">
      <div className="bg-amber-50 border-2 border-amber-200 rounded-3xl p-6 md:p-8 shadow-xl shadow-amber-100/50 max-w-lg w-full" data-id="element-10200">
        <div className="flex items-center gap-4 mb-6" data-id="element-10201">
          <div className="w-16 h-16 flex-shrink-0" data-id="element-10202">
            <DjayIdentity size="md" emotion="pointing" data-id="element-10203" />
          </div>
          <div className="bg-white rounded-2xl p-3 border border-amber-100 shadow-sm relative" data-id="element-10204">
            <div className="absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-white border-l border-b border-amber-100 rotate-45" data-id="element-10205" />
            <p className="font-bold text-amber-900 text-sm md:text-base relative z-10" data-id="element-10206">
              Oeps! Dat was niet helemaal goed. Kijk even naar het juiste
              antwoord.
            </p>
          </div>
        </div>

        <div className="mb-8" data-id="element-10207">
          <AnswerComparison userAnswer={userAnswer} correctAnswer={formatCorrectAnswer(currentQuestion)} isCorrect={false} mode="compact" data-id="element-10208" />
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 justify-end" data-id="element-10209">
          <Button variant="ghost" onClick={onShowFullFeedback} className="w-full sm:w-auto text-amber-700 hover:text-amber-900 hover:bg-amber-100/50" data-id="element-10210">
            Meer uitleg
          </Button>
          <Button variant="primary" onClick={onNext} className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 border-amber-600 text-white shadow-md shadow-amber-500/20" data-id="element-10211">
            Ik snap het, volgende!
            <ArrowRight className="w-4 h-4 ml-2" data-id="element-10212" />
          </Button>
        </div>
      </div>
    </motion.div>;
}